import type { Page, Section } from "@/types/models/page";

export const WEBBUILDER_PREVIEW_PROTOCOL_VERSION = 1 as const;

export type WebbuilderViewportMode = "desktop" | "tablet" | "mobile";

export type PreviewPageMetadata = Pick<
  Page,
  "id" | "name" | "slug" | "status" | "store_id" | "template_id" | "title" | "description"
>;

export type PreviewSnapshot = {
  page: PreviewPageMetadata;
  sections: Section[];
  previewHiddenSectionIds: string[];
  viewportMode: WebbuilderViewportMode;
};

export type PreviewReadyMessage = {
  protocolVersion: typeof WEBBUILDER_PREVIEW_PROTOCOL_VERSION;
  type: "READY";
  draftId: string;
};

export type PreviewInitMessage = {
  protocolVersion: typeof WEBBUILDER_PREVIEW_PROTOCOL_VERSION;
  type: "INIT";
  draftId: string;
  snapshot: PreviewSnapshot;
  revision: number;
};

export type PreviewSnapshotMessage = {
  protocolVersion: typeof WEBBUILDER_PREVIEW_PROTOCOL_VERSION;
  type: "SNAPSHOT";
  snapshot: PreviewSnapshot;
  revision: number;
};

export type PreviewErrorMessage = {
  protocolVersion: typeof WEBBUILDER_PREVIEW_PROTOCOL_VERSION;
  type: "ERROR";
  message: string;
};

export type PreviewSelectSectionMessage = {
  protocolVersion: typeof WEBBUILDER_PREVIEW_PROTOCOL_VERSION;
  type: "SELECT_SECTION";
  sectionId: string | null;
};

export type PreviewUnsupportedFutureMessage = {
  protocolVersion: typeof WEBBUILDER_PREVIEW_PROTOCOL_VERSION;
  type: "REORDER_SECTION" | "UPDATE_SECTION";
  [key: string]: unknown;
};

export type PreviewProtocolMessage =
  | PreviewReadyMessage
  | PreviewInitMessage
  | PreviewSnapshotMessage
  | PreviewErrorMessage
  | PreviewSelectSectionMessage
  | PreviewUnsupportedFutureMessage;

export function createReadyMessage(draftId: string): PreviewReadyMessage {
  return {
    protocolVersion: WEBBUILDER_PREVIEW_PROTOCOL_VERSION,
    type: "READY",
    draftId,
  };
}

export function createInitMessage(input: {
  draftId: string;
  snapshot: PreviewSnapshot;
  revision: number;
}): PreviewInitMessage {
  return {
    protocolVersion: WEBBUILDER_PREVIEW_PROTOCOL_VERSION,
    type: "INIT",
    draftId: input.draftId,
    snapshot: input.snapshot,
    revision: input.revision,
  };
}

export function createSnapshotMessage(input: {
  snapshot: PreviewSnapshot;
  revision: number;
}): PreviewSnapshotMessage {
  return {
    protocolVersion: WEBBUILDER_PREVIEW_PROTOCOL_VERSION,
    type: "SNAPSHOT",
    snapshot: input.snapshot,
    revision: input.revision,
  };
}

export function createErrorMessage(message: string): PreviewErrorMessage {
  return {
    protocolVersion: WEBBUILDER_PREVIEW_PROTOCOL_VERSION,
    type: "ERROR",
    message,
  };
}

export function createSelectSectionMessage(
  sectionId: string | null,
): PreviewSelectSectionMessage {
  return {
    protocolVersion: WEBBUILDER_PREVIEW_PROTOCOL_VERSION,
    type: "SELECT_SECTION",
    sectionId,
  };
}

export function isPreviewProtocolMessage(
  value: unknown,
): value is PreviewProtocolMessage {
  if (!value || typeof value !== "object") return false;

  const message = value as Record<string, unknown>;
  if (message.protocolVersion !== WEBBUILDER_PREVIEW_PROTOCOL_VERSION) {
    return false;
  }

  return (
    message.type === "READY" ||
    message.type === "INIT" ||
    message.type === "SNAPSHOT" ||
    message.type === "ERROR" ||
    message.type === "SELECT_SECTION" ||
    message.type === "REORDER_SECTION" ||
    message.type === "UPDATE_SECTION"
  );
}

export function isSnapshotMessage(
  message: PreviewProtocolMessage,
): message is PreviewInitMessage | PreviewSnapshotMessage {
  return message.type === "INIT" || message.type === "SNAPSHOT";
}

export function isSelectSectionMessage(
  message: PreviewProtocolMessage,
): message is PreviewSelectSectionMessage {
  return message.type === "SELECT_SECTION";
}
